import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import SecondaryMenu from '../../menu/secondaryMenu.component';
import PeriodDropdown from '../../shared/periodDropdown.component';
import Select from '../../shared/reactSelect/select.component';

import { PeriodPropTypes } from '../../shared/propTypes/period';
import { OptionsPropType } from '../../shared/propTypes/common';
import { TabTypes } from '../shared/constants';
import HamburgerDropdown from '../shared/hamburgerDropdown.component';
import Config from '../../config';

import { ModalNames, allCountries } from './constants';
import { getCountryFromLocalStorage, setCountryToLocalStorage } from './bookToTaxDifferencesLocalStorage';
import BookToTaxDifferencesGrid from './bookToTaxDifferencesGrid.component';

import { BookToTaxDifferencePropType, CountryTaxTypePropType, BookToTaxCodeDescriptionPropType } from './propTypes';
import './_bookToTaxDifferencesPage.style.scss';

import {
  fetchInitialDataForBookToTaxDifferences,
  fetchBookToTaxDifferences,
  hideBookToTaxDifferenceModalForDelete,
  removeBookToTaxDifference,
  batchUpdateBookToTaxDifferences,
  addBookToTaxDifference,
  uploadBookToTaxDifferences,
  importBookToTaxDifferences,
  clearUploadedBookToTaxDifferences,
  selectCountryForBookToTaxDifferences,
  refreshBookToTaxDifferences,
} from './store/actions';

import AdminEntityImport from '../shared/adminEntityImport.component';

import {
  periodOrCurrentSelector,
  connectionsOptionsSelector,
  companyCodesOptions,
  bookToTaxCodeEnabledFlagSelector,
  environmentSuffixSelector,
} from '../../shared/store/selectors';
import {
  bookToTaxDifferencesSelector,
  isLoadingInitialDataSelector,
  countryTaxTypesSelector,
  countryOptionsSelector,
  isRemovingBookToTaxDifferenceSelector,
  isBatchUpdatingBookToTaxDifferencesSelector,
  accountsSelector,
  isAddingBookToTaxDifferencesSelector,
  bookToTaxCodeDescriptionsSelector,
  isUploadingBookToTaxDifferencesSelector,
  uploadedBookToTaxDifferencesSelector,
  isImportingBookToTaxDifferencesSelector,
  isLoadingBookToTaxDifferencesSelector,
  isRefreshingBookToTaxDifferencesSelector,
  countrySelector,
} from './store/selectors';
import { modalDataSelector } from '../../shared/modals/modals.selectors';

import { withPermissions, permissions } from '../../shared/authorization';
import downloadXlsx from './downloadXlsx';

import { bookToTaxDifferencesColumnDefintionsForImport } from '../../shared/columnDefinitions/bookToTaxDifferences';
import { hideDecimalsSelector, negativeMinusSelector, displayZeroSelector, userSelector } from '../../userData/store/selectors';
import { useBusinessUnitsOptions } from '../../shared/queries/businessUnits';
import { ViewImportTabs } from '../shared/ViewImportTabs.component';

import { useBookToTaxCodes } from '../../shared/queries/bookToTaxCodes';
import downloadBookToTaxCodeXlsx from '../common/bookToTaxCodes/downloadBookToTaxCodeXlsx';
import BookToTaxCodesModal from './bookToTaxCodesModal.component';

const BookToTaxDifferencePage = props => {
  const {
    period,
    bookToTaxDifferences,
    selectedCountry,
    countries,
    countryTaxTypes,
    connections,
    companyCodes,
    accounts,

    fetchInitialDataForBookToTaxDifferences,
    fetchBookToTaxDifferences,

    deleteModalData,
    removeBookToTaxDifference,
    isRemovingBookToTaxDifference,
    hideBookToTaxDifferenceDeleteModal,

    batchUpdateBookToTaxDifferences,
    isBatchUpdatingBookToTaxDifferences,

    addBookToTaxDifference,
    isAddingBookToTaxDifferences,

    bookToTaxCodeDescriptions,

    bookToTaxCodeEnabledFlag,

    canModifyInSelectedPeriod,

    uploadedBookToTaxDifferences,
    isUploadingBookToTaxDifferences,

    environmentSuffix,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,

    importBookToTaxDifferences,
    uploadBookToTaxDifferences,
    selectCountryForBookToTaxDifferences,

    clearUploadedBookToTaxDifferences,

    isImportingBookToTaxDifferences,
    isLoadingBookToTaxDifferences,

    refreshBookToTaxDifferences,
    isRefreshingBookToTaxDifferences,

    user,
  } = props;
  const { data: businessUnits, isLoading: isLoadingBusinessUnits } = useBusinessUnitsOptions(period.name);

  const isLoadingInitialData = props.isLoadingInitialData || isLoadingBusinessUnits;

  useEffect(() => {
    clearUploadedBookToTaxDifferences();
    fetchInitialDataForBookToTaxDifferences(period.name);
    fetchBookToTaxDifferences(period.name);
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedCountry.countryCode === null) {
      const defaultCountry = {
        value: allCountries.countryCode,
        label: allCountries.countryDescription,
      };
      const localStorageValue = getCountryFromLocalStorage(user && user.lastClientId);
      selectCountryForBookToTaxDifferences(localStorageValue ? localStorageValue : defaultCountry);
    }
  }, [selectedCountry, selectCountryForBookToTaxDifferences, user]);

  const batchUpdate = useCallback(
    (bookToTaxDifferences, actions) => batchUpdateBookToTaxDifferences(
      bookToTaxDifferences,
      { periodName: period.name },
      actions
    ),
    [period, batchUpdateBookToTaxDifferences]
  );

  const add = useCallback(
    (bookToTaxDifferences, actions) => addBookToTaxDifference(
      bookToTaxDifferences,
      { periodName: period.name },
      actions
    ),
    [period, addBookToTaxDifference]
  );

  const refresh = useCallback(() => {
    refreshBookToTaxDifferences({ periodName: period.name });
  }, [period, refreshBookToTaxDifferences]);

  const [activeTab, setActiveTab] = useState(TabTypes.VIEW);

  const selectCountry = useCallback(country => {
    setCountryToLocalStorage(user && user.lastClientId, country);
    selectCountryForBookToTaxDifferences(country);
  }, [selectCountryForBookToTaxDifferences, user]);

  const generateTemplateFileName = useCallback(
    ({ periodName }) => `TLS_Book_To_Tax_Differences_${periodName}`,
    []);

  const fileToImportChange = useCallback(prop => {
    const file = prop.target ? prop.target.files[0] : prop;
    uploadBookToTaxDifferences(file, period, bookToTaxDifferences, accounts);
  }, [uploadBookToTaxDifferences, period, bookToTaxDifferences, accounts]);

  const fileToImportSelect = useCallback(hideDropdown => () => {
    setActiveTab(TabTypes.IMPORT);
    hideDropdown();
  }, []);

  const templateFileName = `${generateTemplateFileName({ periodName: period.name })}${environmentSuffix}.xlsx`;

  const importData = useCallback(
    async bookToTaxDifferences => {
      await importBookToTaxDifferences(
        bookToTaxDifferences,
        { periodName: period.name }
      );
      clearUploadedBookToTaxDifferences();
      setActiveTab(TabTypes.VIEW);
      fetchBookToTaxDifferences(period.name);
    },
    [
      period.name,
      importBookToTaxDifferences,
      clearUploadedBookToTaxDifferences,
      setActiveTab,
      fetchBookToTaxDifferences,
    ]
  );

  const exportToExcel = useCallback(callback => {
    if (bookToTaxDifferences.length) {
      downloadXlsx({
        periodName: period.name,
        bookToTaxDifferences,
        hideDecimals,
        negativeMinus,
        shouldDisplayZero,
        environmentSuffix,
      });
    }
    if (callback) {
      callback();
    }
  }, [
    period,
    bookToTaxDifferences,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
    environmentSuffix,
  ]);

  const renderImportTab = () => (
    <AdminEntityImport
      entities={bookToTaxDifferences}
      uploadedEntities={uploadedBookToTaxDifferences}
      uploadingEntities={isUploadingBookToTaxDifferences || isImportingBookToTaxDifferences}
      selectFileToUpload={fileToImportChange}
      importEntities={importData}
      columnDefs={bookToTaxDifferencesColumnDefintionsForImport}
      templateFileName={templateFileName}
      templatePath={Config.templates.BOOK_TO_TAX_DIFFERENCES}
      pageTitle={'Book To Tax Differences'}
      idField={'exceptionId'}
      promptOnExit
    />
  );


  const renderEditViewGrid = () => (
    <BookToTaxDifferencesGrid
      periodName={period.name}
      countryCode={selectedCountry.countryCode}

      bookToTaxDifferences={bookToTaxDifferences}
      countryTaxTypes={countryTaxTypes}
      connections={connections}
      businessUnits={businessUnits}
      companyCodes={companyCodes}
      accounts={accounts}
      batchUpdateBookToTaxDifferences={batchUpdate}
      addBookToTaxDifference={add}
      bookToTaxCodeDescriptions={bookToTaxCodeDescriptions}
      bookToTaxCodeEnabledFlag={bookToTaxCodeEnabledFlag}
      canModify={canModifyInSelectedPeriod}

      isLoadingInitialData={isLoadingInitialData}
      isLoadingBookToTaxDifferences={isLoadingBookToTaxDifferences}
      isImportingBookToTaxDifferences={isImportingBookToTaxDifferences}
      isBatchUpdating={isBatchUpdatingBookToTaxDifferences}
      isAdding={isAddingBookToTaxDifferences}


      deleteModalData={deleteModalData}
      hideBookToTaxDifferenceDeleteModal={hideBookToTaxDifferenceDeleteModal}
      removeBookToTaxDifference={removeBookToTaxDifference}
      isRemovingBookToTaxDifference={isRemovingBookToTaxDifference}

      clientId={user && user.lastClientId}
    />
  );


  const {
    data: bookToTaxCodes,
    isLoading: isLoadingBookToTaxCodes,
  } = useBookToTaxCodes({ periodName: period.name });

  const [isBookToTaxModalOpen, setIsBookToTaxModalOpen] = useState(false);
  const openBookToTaxModal = useCallback(() => setIsBookToTaxModalOpen(true), [setIsBookToTaxModalOpen]);

  const [isExportingBookToTaxCodes, setIsExportingBookToTaxCodes] = useState(false);
  const closeBookToTaxCodesModal = useCallback(() => setIsBookToTaxModalOpen(false), [setIsBookToTaxModalOpen]);

  const exportBookToTaxCodes = useCallback(async () => {
    setIsExportingBookToTaxCodes(true);
    await downloadBookToTaxCodeXlsx({
      periodName: period.name,
      entities: bookToTaxCodes,
      environmentSuffix,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
    });
    setIsExportingBookToTaxCodes(false);
  }, [
    period.name,
    bookToTaxCodes,
    environmentSuffix,
    shouldDisplayZero,
    hideDecimals,
    negativeMinus,
  ]);

  const renderAdditionalHamburgerItems = useCallback(() => (
    [(
      <button
        key="open-model-button"
        className="hamburger-action link-button"
        onClick={openBookToTaxModal}
      >
        View Book to Tax Code Descriptions
      </button>
    ),
    (
      <button
        key="refresh-btt-button"
        className="hamburger-action link-button"
        disabled={isRefreshingBookToTaxDifferences}
        onClick={refresh}
      >
        Refresh Book to Tax Differences
      </button>
    )]
  ), [openBookToTaxModal, refresh, isRefreshingBookToTaxDifferences]);

  return (
    <div className="book-to-tax-differences admin-import-page">
      <SecondaryMenu>
        <PeriodDropdown />
        <Select
          className="select-country"
          options={countries}
          name="country"
          onChange={selectCountry}
          scrollMenuIntoView={false}
          value={selectedCountry.countryCode}
          placeholder="Select a Country..."
          clearable={false}
          virtualized
        />
        <div className="hamburger-dropdown ml-auto text-right">
          <HamburgerDropdown
            canModify={canModifyInSelectedPeriod}
            disableAddingEntity
            fileToImportChange={fileToImportChange}
            fileToImportSelect={fileToImportSelect}
            templatePath={Config.templates.BOOK_TO_TAX_DIFFERENCES}
            exportToExcel={exportToExcel}
            templateFileName={templateFileName}
            disableImporting={isLoadingInitialData}
            disableExporting={isLoadingInitialData}
            additionalItems={renderAdditionalHamburgerItems()}
          />
        </div>
      </SecondaryMenu>
      <div className="page">
        <div className="container-fluid">
          <div className="row no-gutters page-content-container">
            <div className="col">
              <div className="d-flex flex-row justify-content-between">
                <h5>Book to Tax Differences</h5>
              </div>
            </div>
          </div>
          <ViewImportTabs
            canModify={canModifyInSelectedPeriod}
            activeTab={activeTab}
            changeTab={setActiveTab}
            renderViewTab={renderEditViewGrid} // eslint-disable-line react/jsx-no-bind
            renderImportTab={renderImportTab} // eslint-disable-line react/jsx-no-bind
            viewTabOnly={!canModifyInSelectedPeriod}
          />
        </div>
      </div>
      <BookToTaxCodesModal
        isBookToTaxModalOpen={isBookToTaxModalOpen}
        closeBookToTaxCodesModal={closeBookToTaxCodesModal}
        isLoadingBookToTaxCodes={isLoadingBookToTaxCodes}
        bookToTaxCodes={bookToTaxCodes}
        isExportingBookToTaxCodes={isExportingBookToTaxCodes}
        exportBookToTaxCodes={exportBookToTaxCodes}
      />
    </div>
  );
};


BookToTaxDifferencePage.propTypes = {
  period: PeriodPropTypes.isRequired,
  fetchBookToTaxDifferences: PropTypes.func.isRequired,
  bookToTaxDifferences: PropTypes.arrayOf(BookToTaxDifferencePropType),
  selectedCountry: PropTypes.shape({
    countryCode: PropTypes.string,
  }),
  countries: OptionsPropType,
  countryTaxTypes: PropTypes.arrayOf(CountryTaxTypePropType),
  connections: OptionsPropType,
  companyCodes: OptionsPropType,
  accounts: OptionsPropType,

  isLoadingBookToTaxDifferences: PropTypes.bool.isRequired,
  isLoadingInitialData: PropTypes.bool.isRequired,

  hideBookToTaxDifferenceDeleteModal: PropTypes.func.isRequired,
  removeBookToTaxDifference: PropTypes.func.isRequired,
  isRemovingBookToTaxDifference: PropTypes.bool,

  deleteModalData: PropTypes.shape({
    data: PropTypes.shape({
      rowIndex: PropTypes.number,
      entity: BookToTaxDifferencePropType,
    }),
  }),

  isBatchUpdatingBookToTaxDifferences: PropTypes.bool.isRequired,
  batchUpdateBookToTaxDifferences: PropTypes.func.isRequired,

  fetchInitialDataForBookToTaxDifferences: PropTypes.func.isRequired,

  isAddingBookToTaxDifferences: PropTypes.bool.isRequired,
  addBookToTaxDifference: PropTypes.func.isRequired,
  bookToTaxCodeDescriptions: PropTypes.arrayOf(BookToTaxCodeDescriptionPropType),

  bookToTaxCodeEnabledFlag: PropTypes.bool.isRequired,

  canModifyInSelectedPeriod: PropTypes.bool.isRequired,
  environmentSuffix: PropTypes.string.isRequired,
  shouldDisplayZero: PropTypes.bool.isRequired,
  hideDecimals: PropTypes.bool.isRequired,
  negativeMinus: PropTypes.bool.isRequired,

  uploadBookToTaxDifferences: PropTypes.func.isRequired,
  importBookToTaxDifferences: PropTypes.func.isRequired,
  selectCountryForBookToTaxDifferences: PropTypes.func.isRequired,

  isUploadingBookToTaxDifferences: PropTypes.bool.isRequired,
  uploadedBookToTaxDifferences: PropTypes.arrayOf(BookToTaxDifferencePropType),

  isImportingBookToTaxDifferences: PropTypes.bool.isRequired,
  refreshBookToTaxDifferences: PropTypes.func.isRequired,
  isRefreshingBookToTaxDifferences: PropTypes.bool.isRequired,
  clearUploadedBookToTaxDifferences: PropTypes.func.isRequired,

  user: PropTypes.shape({
    lastClientId: PropTypes.number,
  }),
};


const mapStateToProps = state => ({
  period: periodOrCurrentSelector(state),
  bookToTaxDifferences: bookToTaxDifferencesSelector(state),
  selectedCountry: countrySelector(state),
  countries: countryOptionsSelector(state),
  countryTaxTypes: countryTaxTypesSelector(state),
  connections: connectionsOptionsSelector(state),
  companyCodes: companyCodesOptions(state),
  accounts: accountsSelector(state),

  isLoadingBookToTaxDifferences: isLoadingBookToTaxDifferencesSelector(state),
  isLoadingInitialData: isLoadingInitialDataSelector(state),

  deleteModalData: modalDataSelector(state, ModalNames.bookToTaxDifferenceDeleteModal),
  isRemovingBookToTaxDifference: isRemovingBookToTaxDifferenceSelector(state),
  isBatchUpdatingBookToTaxDifferences: isBatchUpdatingBookToTaxDifferencesSelector(state),
  isAddingBookToTaxDifferences: isAddingBookToTaxDifferencesSelector(state),

  bookToTaxCodeDescriptions: bookToTaxCodeDescriptionsSelector(state),

  bookToTaxCodeEnabledFlag: bookToTaxCodeEnabledFlagSelector(state),

  environmentSuffix: environmentSuffixSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),

  isUploadingBookToTaxDifferences: isUploadingBookToTaxDifferencesSelector(state),
  uploadedBookToTaxDifferences: uploadedBookToTaxDifferencesSelector(state),

  isImportingBookToTaxDifferences: isImportingBookToTaxDifferencesSelector(state),

  isRefreshingBookToTaxDifferences: isRefreshingBookToTaxDifferencesSelector(state),

  user: userSelector(state),
});

export default withPermissions(permissions.BOOK_TO_TAX_DIFFERENCE)(
  connect(mapStateToProps, {
    fetchInitialDataForBookToTaxDifferences,
    fetchBookToTaxDifferences,
    hideBookToTaxDifferenceDeleteModal: hideBookToTaxDifferenceModalForDelete,
    removeBookToTaxDifference,
    batchUpdateBookToTaxDifferences,
    addBookToTaxDifference,
    uploadBookToTaxDifferences,
    importBookToTaxDifferences,
    clearUploadedBookToTaxDifferences,
    selectCountryForBookToTaxDifferences,
    refreshBookToTaxDifferences,
  })(BookToTaxDifferencePage)
);
